import React, { Fragment, FC } from 'react';
import {
  Box,
  IconButton,
  PopoverMenu,
  Text,
  Divider,
  ListItemSelect,
} from '@wix/design-system';
import { More, Delete, Visible, ChangeOrder } from '@wix/wix-ui-icons-common';

import { useTranslation } from '../../useTranslation';
import { useInstalledPlugins, useWidgetPluginManager } from '../hooks';
import { WidgetPlugin, WidgetSlot } from '../../../types/widgetPlugins';

import s from './pluginsPanel.module.scss';
import { PluginLogo } from './pluginLogo';
import { usePluginsBi } from '../hooks/useBi';

const slotHighlightRevertMs = 2000;

export interface InstalledPluginProps {
  plugin: WidgetPlugin;
}

export const InstalledPluginPopoverMenu: FC<InstalledPluginProps> = ({
  plugin,
}) => {
  const { isPluginMoveActionEnabled, showOnPage, uninstallPlugin, movePlugin } =
    useWidgetPluginManager();
  const [t] = useTranslation();

  const showMovePluginAction = isPluginMoveActionEnabled(plugin.slot);

  return (
    <Box>
      <PopoverMenu
        appendTo="scrollParent"
        textSize="small"
        placement="left"
        dataHook="widgetPlugins.installedPlugins.moreActions"
        triggerElement={({ toggle }) => (
          <IconButton
            priority="secondary"
            dataHook="widgetPlugins.installedPlugins.moreActionsOpen"
            skin="standard"
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              toggle();
            }}
          >
            <More />
          </IconButton>
        )}
      >
        {showMovePluginAction && (
          <PopoverMenu.MenuItem
            text={t('PLATFORM_Widget_Slots_Move_Plugin')}
            prefixIcon={<ChangeOrder />}
            onClick={() => movePlugin(plugin.slot as WidgetSlot)}
            dataHook="widgetPlugins.installedPlugins.action.movePlugin"
          />
        )}

        <PopoverMenu.MenuItem
          text={t('PLATFORM_Widget_Slots_Remove_Plugin')}
          prefixIcon={<Delete />}
          onClick={() => {
            uninstallPlugin(plugin.slot as WidgetSlot, {
              origin: 'plugin_manage_card',
            });
          }}
          dataHook="widgetPlugins.installedPlugins.action.delete"
        />

        <PopoverMenu.MenuItem
          text={t('PLATFORM_Widget_Slots_Show_Plugin_On_Page')}
          prefixIcon={<Visible />}
          onClick={() => showOnPage(plugin.slot, slotHighlightRevertMs)}
          dataHook="widgetPlugins.installedPlugins.action.showOnPage"
        />
      </PopoverMenu>
    </Box>
  );
};

export const InstalledPluginListItem: FC<InstalledPluginProps> = ({
  plugin,
}) => {
  const { highlightSlot } = useWidgetPluginManager();

  return (
    <ListItemSelect
      size="small"
      onClick={() => highlightSlot(plugin.slot, slotHighlightRevertMs)}
      prefix={
        <Box verticalAlign="middle" gap="SP2" width={300}>
          <PluginLogo
            plugin={plugin}
            width={29}
            height={29}
            fallbackLogoWidth={31}
            fallbackLogoHeight={31}
          />

          <Text size="small">{plugin.name}</Text>
        </Box>
      }
      dataHook={`widgetPlugins.installedPlugins.pluginId:${plugin.id}`}
      suffix={<InstalledPluginPopoverMenu plugin={plugin} />}
      className={s.installedPluginListItemContainer}
    />
  );
};

export const InstalledPlugins = () => {
  const installedPlugins = useInstalledPlugins();
  const [t] = useTranslation();
  const bi = usePluginsBi();

  if (!installedPlugins.length) {
    return null;
  }

  const handleMouseEnter = (pluginId: string) => {
    bi.pluginHover({
      origin: 'plugin_manage_card',
      action_context: pluginId,
    });
  };

  return (
    <Box marginBottom="SP5" direction="vertical">
      <Box
        direction="vertical"
        background="#fff"
        padding="SP2 SP3"
        borderTopLeftRadius={6}
        borderTopRightRadius={6}
        border="1px solid #dfe5eb"
        borderBottom="none"
      >
        <Text size="small" weight="bold" as="h3">
          {t('PLATFORM_Widget_Slots_Installed_Plugins')}
        </Text>
      </Box>

      <Divider />

      <Box
        direction="vertical"
        background="#fff"
        borderBottomLeftRadius={6}
        borderBottomRightRadius={6}
        border="1px solid #dfe5eb"
        borderTop="none"
      >
        {installedPlugins.map((plugin, index) => (
          <div
            key={plugin.appDefinitionId}
            onMouseEnter={() => {
              handleMouseEnter(plugin.appDefinitionId);
            }}
          >
            <InstalledPluginListItem plugin={plugin} />

            {index + 1 !== installedPlugins.length && <Divider />}
          </div>
        ))}
      </Box>
    </Box>
  );
};
